'use client';

/**
 * BookOnlineCard — prominent Bookeasy CTA shown in the same slot the
 * PartnerAvailabilityWidget used to occupy on a campsite detail page.
 *
 * Renders only when a `booking_url` is set on the entity. We can't show
 * live availability until the upstream Bookeasy API is restored (see
 * project_bookeasy_sync_paused.md), so until then this is the working
 * booking entry-point at the top of the page.
 */

import React from 'react';
import { trackGenerateLead } from '../lib/analytics';

// Same beacon-tracking shape as CampsiteActions.trackBookingClick.
// Inlined rather than extracted to keep this fix surgical.
function trackBookingClick(
  clickType: 'booking_link',
  entityId?: number,
  entityName?: string,
  campsiteType?: string,
) {
  try {
    const payload = JSON.stringify({
      clickType,
      entityId,
      entityName: entityName || '',
      campsiteType: campsiteType || '',
    });
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      navigator.sendBeacon(
        'https://app.trip-planner.com.au/api/products/track-booking-click',
        new Blob([payload], { type: 'application/json' }),
      );
    } else {
      fetch('https://app.trip-planner.com.au/api/products/track-booking-click', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true,
      }).catch(() => {});
    }
  } catch {
    // Never block the user
  }
}

interface BookOnlineCardProps {
  entityId: number;
  entityName: string;
  campsiteType?: string;
  bookingUrl?: string | null;
}

export default function BookOnlineCard({
  entityId,
  entityName,
  campsiteType,
  bookingUrl,
}: BookOnlineCardProps) {
  if (!bookingUrl) return null;

  return (
    <section
      aria-label="Book online"
      className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden"
    >
      <div className="px-5 py-4 flex items-center gap-4">
        <span
          className="inline-flex h-10 w-10 items-center justify-center rounded-full"
          style={{ backgroundColor: 'rgba(34,197,94,0.12)' }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <rect x="3" y="4" width="18" height="18" rx="2" />
            <path d="M16 2v4M8 2v4M3 10h18" />
            <path d="M9 16l2 2 4-4" />
          </svg>
        </span>
        <div className="flex-1 min-w-0">
          <h2 className="text-base font-semibold text-slate-900">Book online</h2>
          <p className="text-sm text-slate-600">
            Check rates and reserve your stay through the operator's booking page.
          </p>
        </div>
        <a
          href={bookingUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            trackBookingClick('booking_link', entityId, entityName, campsiteType);
            trackGenerateLead({
              entityId,
              entityName,
              entityType: campsiteType,
              leadType: 'booking_link',
              bookingUrl,
            });
          }}
          className="inline-flex items-center gap-2 bg-green-600 text-white px-5 py-2.5 rounded-lg font-semibold hover:bg-green-700 transition-colors whitespace-nowrap"
        >
          Book Online
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M7 17L17 7M7 7h10v10" />
          </svg>
        </a>
      </div>
    </section>
  );
}
